import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { FILENAME_REGEX } from '@constants/regex';
import {
  TRAIN_AI_DUPLICATE_QUESTION_LISTING_ROUTE,
  TRAIN_AI_PAGE_ROUTE,
} from '@constants/routes';
import type {
  DataSourceFileContentDTO,
  LLMBlockSelectionDTO,
} from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import {
  DATA_SOURCES_S3_BASE_PATH,
  DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM,
} from '@conversed/rest/src/contracts/data-sources/data-sources.schema';
import { LLMUsageCheckResultStatus } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type {
  TConversationDetailsSchema,
  TDataSourceGenerationResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import { toastSuccessTranslated } from '@helpers/toast';
import { useDataSourceActions } from '@hooks/dataSources/useDataSourceActions';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useLLMUsagesActions } from '@hooks/llmUsages/useLLMUsagesActions';
import { useModal } from '@hooks/modal/useModal';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import type {
  DataSourceGenerationModalProps,
  DataSourceSelectionProps,
} from '@interfaces/TDataSourceGeneration';
import { mainActions } from '@store/reducers/mainHandler';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import DataSourceAutomaticallyIncludeInLLMContent from './DataSourceAutomaticallyIncludeInLLMContent';
import DataSourceGenerationSummaryContent from './DataSourceGenerationSummaryContent';
import DataSourceSelectionContent from './DataSourceSelectionContent';
import DataValidationContent from './DataValidationContent';

const STEPS = 4;

export const DataSourceGenerationModal: React.FC<
  DataSourceGenerationModalProps
> = ({ selectedWeeks, selectedYear, configId, llmBlockId, hotTopicId }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();
  const navigate = useNavigate();
  const [conversations, setConversations] =
    useState<TDataSourceGenerationResponse>();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [dataSourceSelection, setDataSourceSelection] =
    useState<DataSourceSelectionProps>({
      useExistingDataSourceFile: true,
      existingFile: undefined,
      newFileName: '',
    });
  const { hasUseAIKnowledgeLabels } = usePlatformFeatures();

  // If the company is using the AI Knowledge Labels feature, automatically set "automaticallyIncludeInLLM" to false.
  // This is because when using labels, users must manually select labels instead of auto-including files in LLM.
  const [automaticallyIncludeInLLM, setAutomaticallyIncludeInLLM] = useState(
    hasUseAIKnowledgeLabels ? false : DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM,
  );
  const [selectedLLMBlocks, setSelectedLLMBlocks] = useState<
    LLMBlockSelectionDTO[]
  >([]);
  const dispatch = useDispatch();
  const { createDataSourceFile, updateExistingDataSourceFile } =
    useDataSourceActions();
  const { dataSources, priorityFolderId, validSelectablePriorityDataSources } =
    useDataSources();
  const { bulkUpdateCheckResult } = useLLMUsagesActions();

  // Partition the reviewed items so the regular and priority targets are validated/routed
  // independently. Priority items carry checkResult.isPriorityKnowledge.
  const factCheckedData = conversations?.factCheckedData ?? [];
  const priorityItemCount = factCheckedData.filter(
    (item) => item.checkResult?.isPriorityKnowledge,
  ).length;
  const hasPriorityItems = priorityItemCount > 0;
  const hasRegularItems = factCheckedData.length - priorityItemCount > 0;

  // Validates a new file name: required, valid format, and not already taken.
  const validateNewFileName = useCallback(
    (name: string | undefined): string | null => {
      const trimmed = name?.trim();
      if (!trimmed) {
        return t(
          'train-ai.data-source-generation.data-source-selection.new-file-name.not-exist-error',
        );
      }
      if (!FILENAME_REGEX.test(trimmed)) {
        return t(
          'train-ai.data-source-generation.data-source-selection.new-file-name.invalid-error',
        );
      }
      const isFileAlreadyExists = dataSources.some((dataSource) => {
        const keyWithoutDataSourcePaths = dataSource?.key.replace(
          `${DATA_SOURCES_S3_BASE_PATH}/${dataSource?.companyId}/`,
          '',
        );
        return (
          keyWithoutDataSourcePaths === `${trimmed}.xlsx` ||
          trimmed === dataSource.name
        );
      });
      if (isFileAlreadyExists) {
        return t('data-sources.upload.duplicate-file-toast-message', {
          fileName: trimmed,
        });
      }
      return null;
    },
    [t, dataSources],
  );

  const validationDataSourceSelection = useCallback(() => {
    const isUpdateMode = dataSourceSelection.useExistingDataSourceFile;

    // Regular (Train A.I.) target - skipped when the selection has only priority items.
    if (hasRegularItems) {
      if (isUpdateMode) {
        if (!dataSourceSelection?.existingFile?.key) {
          setDataSourceSelection((prev) => ({
            ...prev,
            error: t(
              'train-ai.data-source-generation.data-source-selection.existing-file-key.error',
            ),
          }));
          return false;
        }
      } else {
        const regularError = validateNewFileName(
          dataSourceSelection.newFileName,
        );
        if (regularError) {
          setDataSourceSelection((prev) => ({ ...prev, error: regularError }));
          return false;
        }
      }
    }

    // Priority target - shown only when there are priority items. In update mode it uses
    // an existing priority file when one exists, otherwise falls back to a new file name.
    if (hasPriorityItems) {
      const priorityUsesExisting =
        isUpdateMode && validSelectablePriorityDataSources.length > 0;
      if (priorityUsesExisting) {
        if (!dataSourceSelection?.priorityExistingFile?.key) {
          setDataSourceSelection((prev) => ({
            ...prev,
            priorityError: t(
              'train-ai.data-source-generation.data-source-selection.priority.existing.error',
            ),
          }));
          return false;
        }
      } else {
        const priorityError = validateNewFileName(
          dataSourceSelection.priorityNewFileName,
        );
        if (priorityError) {
          setDataSourceSelection((prev) => ({ ...prev, priorityError }));
          return false;
        }
      }
    }

    return true;
  }, [
    dataSourceSelection,
    hasRegularItems,
    hasPriorityItems,
    validSelectablePriorityDataSources,
    validateNewFileName,
    t,
  ]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    // Validate data source selection step before moving to the next step (Step 4) - Automatically include in LLM step.
    if (currentStep === 2 && !validationDataSourceSelection()) {
      return;
    }

    if (currentStep < STEPS) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, validationDataSourceSelection]);

  const handleFinish = useCallback(async () => {
    const allFactChecked = conversations?.factCheckedData || [];
    const priorityFactChecked = allFactChecked.filter(
      (item) => item.checkResult?.isPriorityKnowledge,
    );
    const regularFactChecked = allFactChecked.filter(
      (item) => !item.checkResult?.isPriorityKnowledge,
    );

    const isUpdateMode = dataSourceSelection.useExistingDataSourceFile;
    const blocks = selectedLLMBlocks.length > 0 ? selectedLLMBlocks : undefined;

    // Corrected answers keep the (corrected) source text; everything else prefers the
    // translated text when present.
    const toFileContent = (
      items: TConversationDetailsSchema[],
    ): DataSourceFileContentDTO[] =>
      items.map((item) => {
        const isCorrected =
          item.checkResult?.status ===
          LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT;
        return {
          question: item.translation?.question ?? item.question,
          answer: isCorrected
            ? item.answer
            : (item.translation?.answer ?? item.answer),
          url: item.url,
          categories: item.categories,
          keywords: item.keywords?.join(', '),
        };
      });

    // Track whether the run created and/or updated file(s) so the success toast matches
    // what actually happened (a single run can update one file and create another).
    let didCreate = false;
    let didUpdate = false;

    // Regular (Train A.I.) target.
    if (regularFactChecked.length) {
      if (isUpdateMode && dataSourceSelection.existingFile?.key) {
        await updateExistingDataSourceFile(
          dataSourceSelection.existingFile,
          toFileContent(regularFactChecked),
          automaticallyIncludeInLLM,
          blocks,
        );
        didUpdate = true;
      } else if (!isUpdateMode && dataSourceSelection.newFileName) {
        await createDataSourceFile(
          dataSourceSelection.newFileName,
          regularFactChecked,
          automaticallyIncludeInLLM,
          blocks,
        );
        didCreate = true;
      }
    }

    // Priority Knowledge target. Update an existing priority file when one exists in
    // update mode; otherwise create a new file in the Priority folder.
    if (priorityFactChecked.length) {
      const priorityUsesExisting =
        isUpdateMode && validSelectablePriorityDataSources.length > 0;
      if (
        priorityUsesExisting &&
        dataSourceSelection.priorityExistingFile?.key
      ) {
        await updateExistingDataSourceFile(
          dataSourceSelection.priorityExistingFile,
          toFileContent(priorityFactChecked),
          automaticallyIncludeInLLM,
          blocks,
        );
        didUpdate = true;
      } else if (dataSourceSelection.priorityNewFileName) {
        await createDataSourceFile(
          dataSourceSelection.priorityNewFileName,
          priorityFactChecked,
          automaticallyIncludeInLLM,
          blocks,
          priorityFolderId,
        );
        didCreate = true;
      }
    }

    if (didUpdate && didCreate) {
      toastSuccessTranslated(
        'train-ai.data-source-generation.files-saved-success',
      );
    } else if (didUpdate) {
      toastSuccessTranslated(
        'train-ai.data-source-generation.updated-existing-file-success',
      );
    } else if (didCreate) {
      toastSuccessTranslated(
        'train-ai.data-source-generation.created-new-file-success',
      );
    }
    hideModal();
  }, [
    createDataSourceFile,
    dataSourceSelection,
    conversations,
    hideModal,
    updateExistingDataSourceFile,
    validSelectablePriorityDataSources,
    priorityFolderId,
    automaticallyIncludeInLLM,
    selectedLLMBlocks,
  ]);

  const handleClickCorrectDuplicates = useCallback(async () => {
    // Automatically exclude secondary duplicates (all but the first in each group)
    // so re-running training doesn't flag the same questions as duplicates again.
    const secondaryUpdates =
      conversations?.duplicateQuestions?.flatMap((group) =>
        group.indexes.slice(1).map((index) => ({
          id: index.id,
          status:
            index.details.checkResult?.status ?? LLMUsageCheckResultStatus.GOOD,
          correctedAnswer: index.details.checkResult?.correctedAnswer || '',
          correctedURL: index.details.checkResult?.correctedURL || '',
          reportIssue: index.details.checkResult?.reportIssue || false,
          reportIssueParams: index.details.checkResult?.reportIssueParams,
          useForAIKnowledgeData: false,
        })),
      ) ?? [];

    if (secondaryUpdates.length > 0) {
      await bulkUpdateCheckResult(secondaryUpdates);
    }

    // Build a set of secondary IDs so we can mark them in the checklist state.
    const secondaryIds = new Set(secondaryUpdates.map((u) => u.id));

    // Flatten all duplicate items; mark secondary ones as excluded so the
    // duplicate checklist renders them grey immediately without a refetch.
    const duplicateQuestions =
      conversations?.duplicateQuestions?.flatMap((group) =>
        group.indexes.map((index) => {
          if (!secondaryIds.has(index.id)) {
            return index.details;
          }
          return {
            ...index.details,
            isEvaluable: false,
            checkResult: {
              ...index.details.checkResult,
              useForAIKnowledgeData: false,
            },
          } as TConversationDetailsSchema;
        }),
      ) || [];

    dispatch(mainActions.setDuplicateQuestionChecklist(duplicateQuestions));
    hideModal();
    navigate({
      pathname: `${TRAIN_AI_PAGE_ROUTE}${TRAIN_AI_DUPLICATE_QUESTION_LISTING_ROUTE}`,
    });
  }, [navigate, conversations, hideModal, dispatch, bulkUpdateCheckResult]);

  return (
    <form className='tailwind-modal-form flex-1 h-[590px] data-source-generation'>
      <div className='tailwind-modal-body flex flex-col gap-3 h-[590px]'>
        {/* Steps */}
        <div className='flex gap-2 items-center mb-3'>
          {[...Array(STEPS)].map((_, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 ${index < (STEPS - 1) ? 'flex-1' : ''}`}>
              <div
                className={`flex rounded px-2 text-white ${
                  currentStep === index + 1
                    ? 'bg-primary-base'
                    : currentStep > index + 1
                      ? 'bg-primary-dark'
                      : 'bg-gray-400'
                }`}>
                {index + 1}
              </div>

              {index < STEPS - 1 && (
                <div className='flex-1 h-1 bg-gray-200 rounded-full' />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 - Data validation */}
        {currentStep === 1 && (
          <DataValidationContent
            selectedWeeks={selectedWeeks}
            configId={configId}
            llmBlockId={llmBlockId}
            selectedYear={selectedYear}
            hotTopicId={hotTopicId}
            conversations={conversations}
            setConversations={setConversations}
          />
        )}

        {/* Step 2 - Data source selection */}
        {currentStep === 2 && (
          <DataSourceSelectionContent
            dataSourceSelection={dataSourceSelection}
            setDataSourceSelection={setDataSourceSelection}
            hasRegularItems={hasRegularItems}
            hasPriorityItems={hasPriorityItems}
          />
        )}

        {/* Step 3 - Automatically include in LLM */}
        {currentStep === 3 && (
          <DataSourceAutomaticallyIncludeInLLMContent
            automaticallyIncludeInLLM={automaticallyIncludeInLLM}
            setAutomaticallyIncludeInLLM={setAutomaticallyIncludeInLLM}
            selectedLLMBlocks={selectedLLMBlocks}
            setSelectedLLMBlocks={setSelectedLLMBlocks}
            dataSourceSelection={dataSourceSelection}
            isDisabled={hasUseAIKnowledgeLabels}
          />
        )}

        {/* Step 4 - Summary */}
        {currentStep === 4 && (
          <DataSourceGenerationSummaryContent
            factCheckedData={conversations?.factCheckedData}
          />
        )}
      </div>
      <div className='tailwind-modal-footer min-h-[33px] animated-element select-none'>
        {conversations &&
          (conversations?.duplicateQuestions?.length > 0 ? (
            <>
              <ButtonT
                onClick={hideModal}
                type='button'
                styling='cancel'
                size='xs'
                rounding='md'>
                {t('general.buttons.cancel')}
              </ButtonT>

              <ButtonT
                type='button'
                styling='primary'
                size='xs'
                rounding='md'
                onClick={() => {
                  handleClickCorrectDuplicates();
                }}>
                {t('general.buttons.correct-duplicates')}
              </ButtonT>
            </>
          ) : (
            <>
              {currentStep > 1 ? (
                <ButtonT
                  onClick={handlePrevious}
                  type='button'
                  styling='cancel'
                  size='xs'
                  rounding='md'>
                  {t('general.buttons.previous')}
                </ButtonT>
              ) : (
                <ButtonT
                  onClick={hideModal}
                  type='button'
                  styling='cancel'
                  size='xs'
                  rounding='md'>
                  {t('general.buttons.cancel')}
                </ButtonT>
              )}
              {conversations?.factCheckedData?.length > 0 &&
                (currentStep === STEPS ? (
                  <ButtonT
                    type='button'
                    styling='primary'
                    size='xs'
                    rounding='md'
                    onClick={handleFinish}>
                    {t('train-ai.data-source-generation.publish-data-source')}
                  </ButtonT>
                ) : (
                  <ButtonT
                    type='button'
                    styling='primary'
                    size='xs'
                    rounding='md'
                    disabled={
                      currentStep === 3 &&
                      automaticallyIncludeInLLM &&
                      !hasUseAIKnowledgeLabels &&
                      selectedLLMBlocks.length === 0
                    }
                    onClick={handleNext}>
                    {t('general.buttons.next')}
                  </ButtonT>
                ))}
            </>
          ))}
      </div>
    </form>
  );
};

export default DataSourceGenerationModal;
